<script setup>
import { computed, onBeforeUnmount, reactive, ref, defineProps } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { schemaMaker, reqString, validateForms } from '@valid/JoiValidation'
import { addCustomer, deleteCustomer } from '@service/CustomerService'
import { createTicket } from '@service/TicketService'
import useLabels from '@composable/labelHandler'
import CustomerShowHeader from './subComponents/CustomerShowHeader'
import CustomerShowInfo from './subComponents/CustomerShowInfo'
import CustomerShowButtons from './subComponents/CustomerShowButtons'
import OurInput from '@common/OurInput'

const props = defineProps(['id'])
const modals = reactive({
  delete: false,
  edit: false,
  label: false,
  newLabel: false
})
const btnLoading = ref(false)
const store = useStore()
const router = useRouter()
const customerGender = ref(null)
store.dispatch('customer/fetchItem', { part: 'customer', id: props.id })

// Get main info from store
const customer = computed(() => store.state.customer.item)
const pageStatus = computed(() => store.state.customer.pageStatus)

// Make offer for customer
// const handleMakeOffer = () => {
//   store.dispatch('offer/handleEnterOffer', {
//     offerType: 'single',
//     customerId: props.id,
//     name: customer.value['name']
//   })
// }

// Make a bonus for customer
const handleMakeBonus = () => {
  const bonusOptions = {
    bonusType: 'single',
    customerId: props.id,
    name: customer.value['name']
  }
  store.dispatch('bonus/handleEnterBonus', bonusOptions)
}

// Edit customer
const customerName = ref('')
const initEditCustomer = () => {
  customerName.value = customer.value?.name
  modals['edit'] = true
}

const validCustomer = schemaMaker({
  name: reqString('نام مشتری')
})

const editCustomer = async (gender) => {
  customerGender.value = gender

  // get items
  const formItems = { name: customerName.value }
  const errors = validateForms(formItems, validCustomer)

  if (errors && errors.length > 0) {
    for (let key of errors) {
      const message = { type: 'error', message: key }
      store.dispatch('addToast', message)
    }
    return
  }

  btnLoading.value = true
  formItems['gender'] = gender

  try {
    await addCustomer(props.id, formItems)

    store.dispatch('addToast', {
      type: 'success',
      message: 'تغییرات مورد نظر با موفقیت اعمال شد.'
    })
    store.dispatch('customer/fetchItem', { part: 'customer', id: props.id })
    btnLoading.value = false
    modals['edit'] = false
  } catch (ex) {
    if (ex.response) {
      store.dispatch('addToast', {
        type: 'status',
        status: ex.response.status
      })
    }
    btnLoading.value = false
    modals['edit'] = false
  }
}

// Delete customer
const handleDeleteCustomer = async () => {
  btnLoading.value = true
  try {
    await deleteCustomer(props.id)
    store.dispatch('addToast', {
      type: 'success',
      message: 'مشتری با موفقیت از سیستم حذف گردید.'
    })
    setTimeout(() => router.push({ name: 'Home' }), 2000)
  } catch (ex) {
    store.dispatch('addToast', {
      type: 'status',
      status: ex.response.status
    })
  } finally {
    btnLoading.value = false
    modals['delete'] = false
  }
}

// Handle labels
const {
  labels,
  getLabels,
  labelAction,
  labelName,
  labelBtnLoading,
  addLabel,
  labelModal,
  newLabelModal,
  deleteLabelModal,
  currentLabel,
  deleteLabel,
  editLabel,
  changeLabel
} = useLabels()

// Initial api call for labels
getLabels()

// Enter to ticket page
const enterTicket = async () => {
  let ticketId
  if (customer.value.ticketId === 0) {
    try {
      const { data } = await createTicket(customer.value.id)
      ticketId = data.ticket_id
    } catch (ex) {
      console.log(ex)
    }
  } else {
    ticketId = customer.value.ticket_id
  }

  router.push({ name: 'TicketShow', params: { id: ticketId } })
}

onBeforeUnmount(() => store.dispatch('customer/clearItem'))
</script>

<template>
  <div class="cmp-wrapper">
    <Navbar msg="پروفایل مشتری" />
    <section
      class="load-pending load-pending--show"
      v-if="pageStatus == 'loading'"
    >
      <img src="@img/vipon-loading.svg" />
    </section>
    <section v-else-if="pageStatus == 'resolved'" class="load-resolved">
      <CustomerShowHeader
        :name="customer?.name"
        :mobile="customer?.mobile"
        :image="customer?.image"
        :gender="customer?.gender"
        :group="customer?.group"
      />
      <CustomerShowInfo
        :id="customer?.id"
        :buy-count="customer?.buy_count"
        :first-buy="customer?.first_buy"
        :last-buy="customer?.last_buy"
        :age="customer?.age"
        :birth-day="customer?.bd_shamsi"
        :city="customer?.city_name"
        :rating="customer?.score"
        :completed="customer?.completed"
        :buy-sum="customer?.buy_sum"
        :pay="customer?.pay"
        :earn="customer?.earn"
        :amount="customer?.amount"
      />
      <CustomerShowButtons
        :id="customer?.id"
        :receiver="customer?.cashier"
        :label="customer?.label"
        @makebonus="handleMakeBonus"
        @deletecustomer="modals['delete'] = true"
        @editcustomer="initEditCustomer"
        @changelabel="labelModal = true"
        @enterticket="enterTicket"
      />
    </section>
    <FailedMode
      @refresh="store.dispatch('customer/fetchItem', { part: 'customer', id })"
      v-else
    />
    <ModalConfirm
      :is-open="modals['delete']"
      :is-loading="btnLoading"
      @modalconfirmclosed="modals['delete'] = false"
      @modalconfirmcancelled="modals['delete'] = false"
      @confirm="handleDeleteCustomer"
      text="آیا از حذف مشتری مطمئن هستید؟"
    />
    <Modal
      type="bottom"
      height="260px"
      :is-open="modals['edit']"
      @modalClosed="modals['edit'] = false"
    >
      <div class="footer-modal-inner">
        <our-input
          label="نام و نام خانوادگی"
          animateType="decrease"
          width="90%"
          ref="input"
          v-model="customerName"
        />
        <div class="genders-btn-container">
          <a class="g-btn" @click="() => editCustomer('1')">
            <svg
              style="shape-rendering:geometricPrecision; text-rendering:geometricPrecision; image-rendering:optimizeQuality; fill-rule:evenodd; clip-rule:evenodd"
              viewBox="0 0 600 512"
            >
              <path
                class="svg-color"
                d="M413.02 162.27c1.23,23.47 1.89,51.17 -22.61,61.08 -0.45,28.9 -14.49,55.52 -38.04,72.35 -3.28,2.34 -6.69,4.45 -10.22,6.32 2.32,7.88 7.11,14.74 13.52,19.63 0.84,0.35 1.58,0.88 2.17,1.55 3.89,2.55 8.27,4.43 13.01,5.44l21.91 3.44c5.23,0.76 10.4,2.1 15.34,3.99 26.89,10.79 44.52,36.87 44.52,65.85 0,0.1 0,0.21 0,0.31l0 62.05c-0.01,3.34 -2.71,6.04 -6.04,6.04l0 0 -293.15 0c-3.33,0 -6.03,-2.7 -6.03,-6.04l0 -62.05c-0.01,-0.43 -0.01,-0.89 -0.01,-1.32 0,-34.65 25.52,-64.01 59.83,-68.82l22.34 -3.5c13.55,-3.04 24.21,-13.12 28.2,-26.07 -4,-2.23 -7.41,-4.74 -11.24,-7.66 -21.94,-16.74 -35.91,-41.82 -36.92,-69.58l-0.02 -0.57 -0.56 -0.13c-12.1,-2.91 -20.8,-13.72 -20.8,-26.22l0 -35.35c-1.42,-65.83 37.85,-116.73 105.77,-119.35 24.55,-0.95 52.75,4.86 74.64,15.92 3.86,1.96 4.45,7.25 1.17,10.05l-1.12 0.95 1.43 0.35c23.81,5.81 43.47,20.31 56.45,41.14 1.5,2.4 1.1,5.79 -0.88,7.8 -7.69,7.8 -12.23,17.91 -12.99,28.73 -0.29,4.18 0.11,9.4 0.33,13.67zm-22.6 46.95c12.24,-11.21 11.62,-29.11 10.53,-46.7 -0.35,-5.61 -0.79,-10.01 -0.28,-15.83 1.01,-11.52 5.43,-22.41 12.85,-31.43l0.36 -0.44 -0.33 -0.47c-13.3,-19.04 -33.99,-31.03 -57.07,-33.51l-11.98 -1.28c-6.21,-0.66 -7.44,-9.26 -1.66,-11.64l7.28 -3 -1.87 -0.65c-38.5,-13.42 -87.07,-12.94 -118.05,16.93 -21.63,20.84 -30.5,52.2 -29.85,81.6 0.1,11.83 0,23.71 0,35.56 0,5.56 3.15,10.57 8.05,13.13l1.11 0.57 -0.01 -1.24c-0.04,-8.33 -0.14,-16.65 -0.19,-24.97 -0.19,-31.05 28.65,-53.87 58.84,-46.63 8.06,1.94 15.6,4.69 23.13,8.15 6.3,2.91 12.39,6.22 18.69,9.11 4.36,2.01 8.53,3.61 12.69,4.83l0.1 0.03 0.32 0.09 0.04 0.02 0.29 0.08 0.17 0.05 0.15 0.04c5.3,1.47 10.6,2.32 16.27,2.61 0.18,0 0.36,0 0.54,0 1.51,0.07 2.99,0.11 4.44,0.11 1.38,0 2.79,-0.04 4.23,-0.11 0.18,0 0.36,0 0.53,0 8.52,-0.43 16.45,-1.59 24.3,-5.3 17.53,-8.28 16.37,4.13 16.38,13.81 0.02,12.16 0.01,24.32 0,36.48zm-12.1 -38.98c-0.02,0.92 -0.03,1.82 -0.03,2.67 0.01,16.34 -0.01,32.69 -0.01,49.03 0,43.18 -35.12,78.31 -78.31,78.31 -43.18,0 -78.31,-35.13 -78.31,-78.31 0,-12.06 -0.14,-24.11 -0.22,-36.17 -0.14,-23.17 21.35,-40.15 43.88,-34.75 12.56,3.01 22.64,8.23 34.08,13.84 6.91,3.39 13.18,6.05 19.46,7.97l0.12 0.03 0.26 0.08c8.05,2.42 16.14,3.62 25.63,3.58 9.71,0.06 17.94,-1.17 26.14,-3.66l0 -0.07c2.43,-0.74 4.85,-1.59 7.31,-2.55zm-109.47 137.14c7.34,2.49 15.09,3.89 22.73,4.66 13.48,1.36 26.96,-0.44 39.48,-5.06 2.61,7.91 7.12,14.98 13.03,20.63l-44.08 41.07 -44.05 -41.09c5.8,-5.55 10.26,-12.46 12.89,-20.21zm31.16 75.59c-0.01,0 -0.01,0 -0.02,0 -1.52,0 -2.99,-0.58 -4.1,-1.63l-49.75 -46.41c-4.4,2.51 -9.22,4.39 -14.36,5.5l-22.63 3.57c-4.35,0.6 -8.63,1.71 -12.73,3.25 -22.31,9.02 -36.92,30.69 -36.92,54.75 0,0.06 0,0.13 0,0.19l0 56.02 281.04 0 0 -55.98c0,-0.08 0,-0.17 0,-0.24 0,-24.07 -14.64,-45.73 -36.97,-54.7 -4.1,-1.54 -8.38,-2.64 -12.72,-3.26l-22.24 -3.5c-5.26,-1.1 -10.2,-3.01 -14.69,-5.58l-49.78 46.41c-1.11,1.03 -2.58,1.61 -4.09,1.61 -0.02,0 -0.03,0 -0.04,0l0 0z"
              />
            </svg>
            <span>مرد</span>
            <spinner v-show="btnLoading && customerGender == '1'" />
            <i class="male" />
          </a>
          <a class="g-btn" @click="() => editCustomer('2')">
            <svg
              style="shape-rendering:geometricPrecision; text-rendering:geometricPrecision; image-rendering:optimizeQuality; fill-rule:evenodd; clip-rule:evenodd"
              viewBox="0 0 600 512"
            >
              <path
                class="svg-color"
                d="M384.67 317.47c-1.6,0 -3.14,-0.64 -4.27,-1.77 -10.65,-10.66 -23.31,-19.14 -37.24,-24.88 -2.34,-0.89 -3.88,-3.13 -3.88,-5.64 0,-3.33 2.7,-6.03 6.04,-6.03 0.84,0 1.67,0.17 2.44,0.52 13.17,5.42 25.36,13.08 35.97,22.58 18.27,-28.48 28.01,-61.62 28.01,-95.46 0,-0.71 -0.02,-1.46 -0.02,-2.15 0,-87.84 -47.15,-149.17 -114.66,-149.17 -67.51,0 -114.75,61.33 -114.75,149.15 0.02,0.7 0,1.48 0,2.19 0,33.84 9.77,66.99 28.07,95.43 10.58,-9.48 22.76,-17.17 35.9,-22.59 0.78,-0.34 1.61,-0.52 2.46,-0.52 3.33,0 6.03,2.7 6.03,6.04 0,2.5 -1.54,4.74 -3.88,5.64 -13.87,5.73 -26.54,14.21 -37.15,24.85 -1.13,1.13 -2.67,1.77 -4.27,1.77 -1.92,0 -3.72,-0.91 -4.85,-2.45 -22.36,-31.49 -34.39,-69.19 -34.39,-107.82 0,-0.84 0.02,-1.73 0.02,-2.55 0,-94.9 52.16,-161.2 126.82,-161.2 74.67,0 126.74,66.3 126.74,161.23 -0.01,0.77 0.01,1.69 0.01,2.51 0,38.61 -12,76.33 -34.28,107.84 -1.05,1.42 -2.66,2.3 -4.41,2.43l-0.46 0.05z"
              />
              <path
                class="svg-color"
                d="M300.71 313.01l-7.37 0c-0.01,0 -0.02,0 -0.03,0 -0.27,0 -0.55,-0.02 -0.83,-0.06 -95.38,-13.65 -84.62,-151.37 -84.51,-152.76 0.15,-1.72 1.02,-3.3 2.41,-4.32 1.05,-0.78 2.32,-1.19 3.62,-1.19 0.41,0 0.81,0.04 1.21,0.12 7.71,1.41 15.6,2.12 23.46,2.12 10.46,0 20.91,-1.27 31.07,-3.76 41.21,-12.07 56.5,-37.24 56.65,-37.5 1.09,-1.84 3.07,-2.98 5.22,-2.98 2.86,0 5.34,2.02 5.91,4.83 0.26,1.21 6.95,30.79 43.59,37.25 2.69,0.48 4.72,2.71 4.95,5.43 0.13,1.39 10.91,139.11 -84.51,152.76 -0.26,0.04 -0.53,0.06 -0.8,0.06 -0.01,0 -0.02,0 -0.04,0zm-6.11 -12.13l6.51 0c74.76,-11.07 74.92,-112.61 74.01,-135.22 -19.42,-3.98 -35.91,-16.8 -44.57,-34.64 -15.36,16.24 -34.99,27.93 -56.59,33.67 -11.12,2.88 -22.64,4.35 -34.16,4.35 -6.44,0 -12.9,-0.47 -19.28,-1.37 -0.74,26.17 1.5,122.46 74.09,133.21l-0.01 0z"
              />
              <path
                class="svg-color"
                d="M444.11 470.32l-293.14 0c-3.34,0 -6.04,-2.7 -6.04,-6.04l0 -62.05c0,-0.43 -0.01,-0.89 -0.01,-1.32 0,-34.65 25.52,-64.01 59.83,-68.82l22.34 -3.5c15.87,-3.55 27.76,-16.77 29.64,-32.92 0.31,-3.08 2.91,-5.44 6.01,-5.44 3.31,0 6.01,2.68 6.03,5.99 0,0.22 0,0.52 -0.03,0.74 -2.49,21.43 -18.35,38.91 -39.42,43.47l-22.64 3.57c-4.34,0.6 -8.62,1.71 -12.73,3.25 -22.31,9.02 -36.92,30.69 -36.92,54.75 0,0.06 0,0.13 0,0.19l0 56.02 281.04 0 0 -55.98c0.01,-0.08 0.01,-0.17 0.01,-0.24 0,-24.07 -14.65,-45.73 -36.97,-54.7 -4.11,-1.54 -8.39,-2.64 -12.73,-3.26l-22.23 -3.5c-21.52,-4.5 -37.64,-22.44 -39.85,-44.31 -0.02,-0.2 -0.03,-0.4 -0.03,-0.6 0,-3.34 2.71,-6.04 6.04,-6.04 3.1,0 5.7,2.35 6.01,5.43 1.65,16.55 13.81,30.14 30.06,33.63l21.92 3.44c5.23,0.76 10.39,2.1 15.33,3.99 26.9,10.79 44.52,36.87 44.52,65.85 0,0.1 0,0.21 0,0.31l0 62.05c0,3.34 -2.7,6.04 -6.04,6.04l0 0z"
              />
              <path
                class="svg-color"
                d="M297.54 382.97c0,0 -0.01,0 -0.02,0 -1.52,0 -2.99,-0.58 -4.1,-1.63l-53.18 -49.62c-1.13,-1.13 -1.76,-2.66 -1.76,-4.26 0,-3.34 2.7,-6.04 6.03,-6.04 1.46,0 2.86,0.53 3.96,1.48l49.07 45.78 49.14 -45.78c1.13,-1.09 2.64,-1.71 4.21,-1.71 3.33,0 6.03,2.71 6.03,6.04 0,1.72 -0.73,3.36 -2.01,4.5l-53.24 49.63c-1.11,1.03 -2.57,1.61 -4.09,1.61 -0.01,0 -0.03,0 -0.04,0l0 0z"
              />
            </svg>
            <span>زن</span>
            <spinner v-show="btnLoading && customerGender == '2'" />
            <i class="female" />
          </a>
        </div>
      </div>
    </Modal>
    <Modal :is-open="labelModal" @modalClosed="labelModal = false">
      <div class="label-modal">
        <div class="credit-buttons" style="margin:20px 0">
          <a
            class="button button--modal"
            @click="
              () => {
                labelModal = false
                newLabelModal = true
                labelAction = 'add'
              }
            "
          >
            <svg class="button__icon" viewBox="0 0 512 512">
              <path
                d="m368 272h-224c-8.832031 0-16-7.167969-16-16s7.167969-16 16-16h224c8.832031 0 16 7.167969 16 16s-7.167969 16-16 16zm0 0"
              />
              <path
                d="m256 384c-8.832031 0-16-7.167969-16-16v-224c0-8.832031 7.167969-16 16-16s16 7.167969 16 16v224c0 8.832031-7.167969 16-16 16zm0 0"
              />
            </svg>
          </a>
        </div>
        <div class="organ">
          <template v-for="label in labels" :key="label.id">
            <div
              class="organ__row"
              @click.self="changeLabel(customer['id'], label)"
            >
              <h5 class="organ__title" v-text="label['name']" />
              <i class="organ__vr"></i>
              <a
                class="organ__btn-edit"
                @click="
                  () => {
                    currentLabel = label
                    labelAction = 'edit'
                    labelName = label.name
                    labelModal = false
                    newLabelModal = true
                  }
                "
              >
                <svg viewBox="0 0 512 512">
                  <path
                    d="M180.36 73.78c0,-27.71 22.65,-50.35 50.35,-50.35l17.62 0 15.34 0 17.62 0c27.7,0 50.35,22.64 50.35,50.35l0 248.72 0 37.95 0 3.93c-21.37,37.05 -42.73,74.11 -64.1,111.16 -9.81,19.64 -14.65,15.07 -23.29,-0.44l-63.89 -111.23 0 -3.42 0 -24.99 0 -261.68zm125.01 283.59l0 -188.65 -98.74 0 0 188.19 49.37 86.12 49.37 -85.66zm-98.74 -214.92l98.74 0 0 -68.67c0,-13.23 -10.84,-24.07 -24.08,-24.07l-17.62 0 -15.34 0 -17.62 0c-13.24,0 -24.08,10.84 -24.08,24.07l0 68.67z"
                  />
                </svg>
              </a>
              <i class="organ__vr"></i>
              <a
                class="organ__btn-delete"
                @click="
                  () => {
                    currentLabel = label
                    labelModal = false
                    deleteLabelModal = true
                  }
                "
                >&times;</a
              >
            </div>
          </template>
          <spinner v-show="modalLoading" />
        </div>
      </div>
    </Modal>
    <Modal
      :is-open="newLabelModal"
      @modalClosed="
        () => {
          newLabelModal = false
          labelModal = true
        }
      "
      height="200px"
    >
      <div class="label-modal" style="padding-top: 0px">
        <div class="label-forms">
          <our-input
            label="نام گروه"
            width="100%"
            animateType="remove"
            starLabel
            pr="5px"
            style="margin-bottom: 20px"
            v-model="labelName"
          />
        </div>
        <div class="credit-buttons buttons--filter">
          <button
            class="button button--filter"
            @click="
              () => {
                newLabelModal = false
                labelModal = true
              }
            "
          >
            <svg class="button__icon" viewBox="0 0 512 512">
              <path
                d="M357.1 154.91l-0.01 -0.01c-5.23,-5.23 -13.8,-5.24 -19.04,0l-82.05 82.05 -82.05 -82.05c-5.24,-5.24 -13.81,-5.23 -19.04,0l-0.01 0.01c-5.23,5.23 -5.24,13.8 0,19.04l82.05 82.05 -82.05 82.05c-5.24,5.24 -5.23,13.81 0,19.04l0.01 0.01c5.23,5.23 13.8,5.24 19.04,0l82.05 -82.05 82.05 82.05c5.24,5.24 13.81,5.23 19.04,0l0.01 -0.01c5.23,-5.23 5.24,-13.8 0,-19.04l-82.05 -82.05 82.05 -82.05c5.24,-5.24 5.23,-13.81 0,-19.04z"
              />
            </svg>
          </button>
          <button
            class="button button--filter button--accept"
            @click="
              () => {
                if (labelAction === 'add') addLabel()
                else if (labelAction === 'edit') editLabel()
              }
            "
          >
            <svg class="button__icon" viewBox="0 0 512 512">
              <path
                d="M394.58 154.91l0 -0.01c-5.24,-5.23 -13.8,-5.24 -19.04,0l-173.63 173.62 -65.45 -65.45c-5.24,-5.24 -13.8,-5.23 -19.04,0l0 0.01c-5.24,5.23 -5.24,13.8 0,19.04l74.97 74.97 0 0.01c5.24,5.23 13.81,5.24 19.05,0l183.14 -183.15c5.24,-5.24 5.24,-13.81 0,-19.04z"
              />
            </svg>
            <spinner v-if="labelBtnLoading" />
          </button>
        </div>
      </div>
    </Modal>
    <ModalConfirm
      :is-open="deleteLabelModal"
      :is-loading="labelBtnLoading"
      @modalconfirmclosed="deleteLabelModal = false"
      @modalconfirmcancelled="
        () => {
          deleteLabelModal = false
          labelModal = true
        }
      "
      @confirm="deleteLabel(currentLabel?.id)"
      :text="`آیا از حذف گروه &quot;${currentLabel?.name}&quot; مطمئن هستید؟`"
    />
    <FixedFooter />
  </div>
</template>

<style scoped lang="scss">
.cmp-wrapper {
  background: linear-gradient(to bottom, #fff 140px, #eee 240px);
  padding-bottom: 70px;
}
.load-pending--show {
  height: calc(100vh - 135px);
}
.label-modal {
  background-color: #eee;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0;
  padding: 0 0 20px;
  width: 100%;
  .buttons--filter {
    justify-content: space-between;
    margin: 0;
    padding: 0 20px;
  }
}
.label-forms {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 90%;
  margin: 0 0 10px;
}
.organ {
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
}
.organ__row {
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 92%;
  height: 56px;
  border-radius: 10px;
  padding: 8px;
  margin-bottom: 10px;
  background: linear-gradient(
    to right,
    #bbb 38px,
    #ddd 38px,
    #ddd 76px,
    #fff 76px
  );
}
.organ__img {
  width: 40px;
  height: 40px;
  border-radius: 5px;
  pointer-events: none;
}
.organ__title {
  color: #111;
  font-size: 16px;
  width: 100%;
  text-align: right;
  pointer-events: none;
  margin-right: 6px;
}
.organ__hr {
  width: 92%;
  height: 1px;
  background-color: #aaa;
  margin: 10px 0;
  &:last-child {
    display: none;
  }
}
.organ__vr {
  height: 100%;
  width: 1px;
  // background-color: #c7c7c7;
  justify-self: center;
  margin: 0 6px;
}
.organ__btn-delete {
  width: 30px;
  height: 30px;
  line-height: 20px;
  font-size: 26px;
  padding: 4px 0 0;
  margin: 0;
  border-radius: 100%;
  color: #888;
  // background-color: #c8c8c8;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-weight: bold;
  position: relative;
  // top: 4px;
  // left: -5px;
}
.organ__btn-edit {
  border-radius: 100%;
  // background-color: #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 30px;
  height: 30px;
  transform: rotate(30deg);
  svg {
    fill: #777;
    width: 26px;
    height: 26px;
  }
}
.button--modal {
  width: 92%;
  background-color: #fff;
}
.button--modal .button__icon {
  fill: #777;
}
</style>
